/* ============================================================
 * NxtLuv email popup + footer signup
 *
 *  - Trigger: 5s timer OR 35% scroll, whichever comes first
 *  - Suppression: 30 days after dismiss/signup (localStorage)
 *  - Submitted profiles never see the popup again
 *  - Klaviyo client-side subscribe (POST /client/subscriptions)
 *  - Accessibility: role=dialog, focus trap, Esc to close, aria-* hooks
 *
 * One IIFE so it survives being loaded twice without rebinding.
 * ============================================================ */
(function () {
  if (window.__nxPopupBooted) return;
  window.__nxPopupBooted = true;

  // ---- Config ----
  var SUPPRESS_DAYS = 30;
  var SUPPRESS_MS   = SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
  var DELAY_MS      = 5000;
  var SCROLL_PCT    = 0.35;

  var LS_SEEN_UNTIL = 'nxtluv_popup_seen_until';
  var LS_SUBMITTED  = 'nxtluv_popup_submitted';

  var PLACEHOLDER_KEY  = 'REPLACE_ME_PUBLIC_KEY';
  var PLACEHOLDER_LIST = 'REPLACE_ME_LIST_ID';

  // ---- Tiny helpers ----
  function $ (sel, root) { return (root || document).querySelector(sel); }
  function $$ (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function safeLS (fn) { try { return fn(); } catch (e) { return null; } }
  function getLS (k) { return safeLS(function () { return localStorage.getItem(k); }); }
  function setLS (k, v) { return safeLS(function () { localStorage.setItem(k, v); }); }
  function now () { return Date.now(); }
  function isEmail (s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim()); }

  function previewMode () {
    var p = window.location.search || '';
    return /nx_preview=1/.test(p) || document.documentElement.hasAttribute('data-nx-preview');
  }

  // ---- Klaviyo client subscribe ----
  // Reference: https://developers.klaviyo.com/en/reference/create_client_subscription
  function klaviyoSubscribe (publicKey, listId, revision, email, sourceLabel) {
    if (!publicKey || publicKey === PLACEHOLDER_KEY || !listId || listId === PLACEHOLDER_LIST) {
      console.warn('[nxtluv] Klaviyo config missing — popup is in diagnostic mode. Fill snippets/email-popup-config.liquid.');
      return Promise.resolve({ ok: false, status: 0, reason: 'config_missing' });
    }
    if (previewMode()) {
      console.info('[nxtluv] Preview mode — skipping real Klaviyo call for', email);
      return new Promise(function (resolve) {
        setTimeout(function () { resolve({ ok: true, status: 202, reason: 'preview' }); }, 600);
      });
    }
    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(publicKey);
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: sourceLabel || 'NxtLuv Site',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };
    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Accept': 'application/vnd.api+json',
        'Content-Type': 'application/json',
        'revision': revision || '2024-10-15'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      // Klaviyo returns 202 Accepted on success
      return { ok: res.ok || res.status === 202, status: res.status };
    }).catch(function (err) {
      console.error('[nxtluv] Klaviyo subscribe failed', err);
      return { ok: false, status: 0, error: err && err.message };
    });
  }

  // ============================================================
  // POPUP
  // ============================================================
  function initPopup () {
    var popup = document.getElementById('nx-popup');
    if (!popup) return;

    var card        = $('.nx-popup__card', popup);
    var form        = $('[data-nx-form="popup"]', popup);
    var input       = $('.nx-popup__input', popup);
    var btn         = form ? $('.nx-popup__btn', form) : null;
    var btnLabel    = form ? $('[data-nx-btn-label]', form) : null;
    var btnSpinner  = form ? $('.nx-popup__btn-spinner', form) : null;
    var err         = $('[data-nx-error]', popup);
    var stateForm   = $('[data-nx-state="form"]', popup);
    var stateSucc   = $('[data-nx-state="success"]', popup);
    var closeEls    = $$('[data-nx-close]', popup);

    var publicKey = popup.getAttribute('data-klaviyo-key') || '';
    var listId    = popup.getAttribute('data-klaviyo-list') || '';
    var revision  = popup.getAttribute('data-klaviyo-revision') || '2024-10-15';

    var lastFocused = null;
    var isOpen = false;

    function suppressFor (ms) {
      setLS(LS_SEEN_UNTIL, String(now() + ms));
    }
    function markSubmitted () {
      setLS(LS_SUBMITTED, '1');
      suppressFor(SUPPRESS_MS);
    }
    function shouldShow () {
      if (previewMode()) return true;
      if (getLS(LS_SUBMITTED) === '1') return false;
      var until = parseInt(getLS(LS_SEEN_UNTIL) || '0', 10);
      return now() > until;
    }

    function getFocusable () {
      return $$('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', popup)
        .filter(function (el) {
          return !el.hasAttribute('disabled')
            && !el.hasAttribute('hidden')
            && el.offsetParent !== null
            && el.getAttribute('aria-hidden') !== 'true';
        });
    }

    function open () {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      popup.hidden = false;
      // double-rAF to let the [hidden] removal commit before the transition class
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { popup.classList.add('is-open'); });
      });
      document.body.classList.add('nx-popup-open');
      document.addEventListener('keydown', onKeydown, true);
      setTimeout(function () {
        if (input && !input.hidden) input.focus();
      }, 60);
    }

    function close (reason) {
      if (!isOpen) return;
      isOpen = false;
      popup.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown, true);
      // Hold suppression for 30d on close (per spec)
      suppressFor(SUPPRESS_MS);
      // Wait for transition before hiding to keep close transition smooth
      setTimeout(function () {
        popup.hidden = true;
        document.body.classList.remove('nx-popup-open');
        if (lastFocused && typeof lastFocused.focus === 'function') {
          try { lastFocused.focus(); } catch (e) {}
        }
      }, 240);
    }

    function onKeydown (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        close('esc');
        return;
      }
      if (e.key === 'Tab') {
        var foci = getFocusable();
        if (!foci.length) return;
        var first = foci[0];
        var last  = foci[foci.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    }

    function showSuccess () {
      if (stateForm) stateForm.hidden = true;
      if (stateSucc) stateSucc.hidden = false;
    }

    function setBusy (busy) {
      if (!btn) return;
      btn.disabled = busy;
      if (btnLabel) btnLabel.textContent = busy ? 'Sending…' : 'Get my 10% code';
      if (btnSpinner) btnSpinner.hidden = !busy;
    }

    function setError (msg) {
      if (!err) return;
      if (!msg) { err.hidden = true; err.textContent = ''; input.removeAttribute('aria-invalid'); return; }
      err.textContent = msg;
      err.hidden = false;
      input.setAttribute('aria-invalid', 'true');
    }

    // ---- Event wiring ----
    closeEls.forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); close('click'); });
    });
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        setError('');
        var email = (input.value || '').trim();
        if (!isEmail(email)) {
          setError('Please enter a valid email address.');
          input.focus();
          return;
        }
        setBusy(true);
        klaviyoSubscribe(publicKey, listId, revision, email, 'NxtLuv Site Popup')
          .then(function (result) {
            setBusy(false);
            if (result.ok || result.reason === 'config_missing' || result.reason === 'preview') {
              markSubmitted();
              showSuccess();
            } else {
              setError('Something went wrong. Please try again in a moment.');
            }
          });
      });
    }

    // ---- Trigger ----
    if (!shouldShow()) return;

    var armed = false;
    function arm () {
      if (armed) return;
      armed = true;
      teardownTriggers();
      // Slight delay so it doesn't fight first paint
      requestAnimationFrame(open);
    }
    var timer = setTimeout(arm, DELAY_MS);
    function onScroll () {
      var h = document.documentElement;
      var max = (h.scrollHeight - h.clientHeight) || 1;
      var pct = (h.scrollTop || window.pageYOffset) / max;
      if (pct >= SCROLL_PCT) arm();
    }
    function teardownTriggers () {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    // Expose a tiny dev hook for QA
    window.NxPopup = {
      open: open,
      close: close,
      reset: function () {
        safeLS(function () {
          localStorage.removeItem(LS_SEEN_UNTIL);
          localStorage.removeItem(LS_SUBMITTED);
        });
        console.info('[nxtluv] popup suppression cleared');
      }
    };
  }

  // ============================================================
  // FOOTER SIGNUP
  // ============================================================
  function initFooter () {
    $$('.nx-footer-signup').forEach(function (root) {
      var form    = $('[data-nx-form="footer"]', root);
      if (!form) return;
      var input   = $('.nx-footer-signup__input', form);
      var btn     = $('.nx-footer-signup__btn', form);
      var label   = $('[data-nx-btn-label]', form);
      var spinner = $('.nx-popup__btn-spinner', form);
      var err     = $('[data-nx-error]', root);
      var success = $('[data-nx-footer-success]', root);

      var publicKey = root.getAttribute('data-klaviyo-key') || '';
      var listId    = root.getAttribute('data-klaviyo-list') || '';
      var revision  = root.getAttribute('data-klaviyo-revision') || '2024-10-15';

      function setError (msg) {
        if (!err) return;
        if (!msg) { err.hidden = true; err.textContent = ''; input.removeAttribute('aria-invalid'); return; }
        err.textContent = msg;
        err.hidden = false;
        input.setAttribute('aria-invalid', 'true');
      }
      function setBusy (busy) {
        btn.disabled = busy;
        if (label) label.textContent = busy ? 'Sending…' : 'Subscribe';
        if (spinner) spinner.hidden = !busy;
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        setError('');
        var email = (input.value || '').trim();
        if (!isEmail(email)) {
          setError('Please enter a valid email address.');
          input.focus();
          return;
        }
        setBusy(true);
        klaviyoSubscribe(publicKey, listId, revision, email, 'NxtLuv Footer Signup')
          .then(function (result) {
            setBusy(false);
            if (result.ok || result.reason === 'config_missing' || result.reason === 'preview') {
              form.hidden = true;
              if (success) success.hidden = false;
              // Mark submitted so popup also stops showing
              setLS(LS_SUBMITTED, '1');
              setLS(LS_SEEN_UNTIL, String(now() + SUPPRESS_MS));
            } else {
              setError('Something went wrong. Please try again in a moment.');
            }
          });
      });
    });
  }

  function ready (fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else fn();
  }

  ready(function () {
    initPopup();
    initFooter();
  });
})();
